import React, { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';

import { CameraStateFlags } from '@strava/mre';

import { getScreenPositionOfLatLng } from '../../helpers/getScreenPositionOfLatLng';
import { useMRE } from '../../hooks/useMRE';
import { LatLng } from '../../types';

import styles from './DrawCircleOnMap2D.module.scss';

/*
 * Draws a circle on a 2D map at the specified lat/lng with the given radius in pixels.
 *
 * Note: Circle will only be visible when in top-down view (i.e. not tilted).
 */

export interface DrawCircleOnMap2DProps {
  latLng: LatLng;
  radius: number;
  className?: string;
}

// TODO: need to allow zoom THROUGH the component, possible here or on content
export const DrawCircleOnMap2D = ({
  latLng,
  radius,
  className
}: DrawCircleOnMap2DProps) => {
  const diameter = radius * 2;

  const { mapContainerRef, terrainEngine } = useMRE();

  // We set position by using a ref and applying style modifications rather than
  // re-rendering the container in a new position.
  const drawCircleOnMap2DRef = useRef<HTMLDivElement>(null);

  const updateLocation = useCallback(() => {
    if (!drawCircleOnMap2DRef.current) {
      return;
    }

    if (!terrainEngine || !mapContainerRef.current || !latLng) {
      drawCircleOnMap2DRef.current.style.display = 'none';
      return;
    }

    // Get the screen position of the latLng
    const screenPositionOfLatLng = getScreenPositionOfLatLng({
      container: mapContainerRef.current,
      camera: terrainEngine.getCamera(),
      latLng
    });

    // If no `screenPositionOfLatLng` or `isOccluded`, hide the element
    if (!screenPositionOfLatLng || screenPositionOfLatLng.isOccluded) {
      drawCircleOnMap2DRef.current.style.display = 'none';
      return;
    }

    // Otherwise, show the element
    drawCircleOnMap2DRef.current.style.display = 'initial';

    // Position the element centered at the calculated position. Transform is more performant than top/left etc.
    drawCircleOnMap2DRef.current.style.transform = `translateX(-50%) translateY(-50%) translate(${screenPositionOfLatLng.x}px, ${screenPositionOfLatLng.y}px)`;
  }, [latLng, terrainEngine, mapContainerRef]);

  // when the latLng changes then we trigger an immediate location update
  useEffect(() => {
    if (!latLng || !mapContainerRef.current || !terrainEngine) {
      return;
    }

    updateLocation();
  }, [latLng, mapContainerRef, terrainEngine, updateLocation]);

  // bind updateLocation to camera target or canvas size changes
  useEffect(() => {
    if (!terrainEngine) {
      return;
    }

    const cameraStateListener = {
      stateToObserve: () => CameraStateFlags.TARGET,
      onViewTypeChanged: () => {},
      onTargetChanged: ({ pitch }: { pitch: number }) => {
        // if pitch is 0 (top-down) then we update location
        pitch === 0 && updateLocation();
      }
    };

    terrainEngine.getCamera().addStateListener(cameraStateListener);

    window.addEventListener('resize', updateLocation);

    return () => {
      terrainEngine.getCamera().removeStateListener(cameraStateListener);
      window.removeEventListener('resize', updateLocation);
    };
  }, [terrainEngine, updateLocation]);

  return (
    <div
      className={clsx(styles.container, className)}
      ref={drawCircleOnMap2DRef}
      data-testid="draw-circle-on-map-2d"
    >
      <svg
        width={diameter}
        height={diameter}
        data-testid="draw-circle-on-map-2d-svg"
      >
        <circle
          data-testid="draw-circle-on-map-2d-circle"
          cx={radius}
          cy={radius}
          r={radius}
          fill="#f00"
          stroke="#f00"
          strokeWidth="1"
          opacity="0.35"
          fillOpacity="0.35"
          strokeOpacity="0.5"
          strokeDasharray="5,3"
          strokeDashoffset="2"
        />
      </svg>
    </div>
  );
};
